/**
 * 入住修改
 */
import { OccType, SourceChannelType } from '@/config/constant';
import BaseService from '@/api/checkInBase';
import DateJS, { format2DateStr, format2TimeStr, format2TimeStamp } from '@/utils/date';
import {
  ConfigRoomPrice,
  RemarkInfo,
  EditRentRoomInfo,
  RentTimeInfo,
  SourceChannelInfo,
  StrategyItem,
  EditRoomPriceInfo,
  CouponValue,
} from '@/redux/reducers/reservation/base.d';
import {
  createActionMiddleware,
  flatPriceList,
  getEmptyCouponData,
  getBusinessDate,
  getDayDepartureTime,
  getAllChannelList,
  getChannelList,
  findItemByKey,
  getRentDayArr,
} from '@/redux/actions/reservation/helper';
import {
  ReducerState,
  MainState,
  CommonInfo,
  storeName,
} from '@/redux/reducers/reservation/checkInEditor';

import { CheckInEditorAction } from './constant';

// getState函数覆写
const actionWrapper = createActionMiddleware<ReducerState>(storeName);

// 获取房间定价
const getRoomPriceList: (state: ReducerState) => Promise<ConfigRoomPrice[]> = async (state) => {
  let result: ConfigRoomPrice[] = [];
  try {
    const { channelInfo, rentTimeInfo, rentRoomInfos, commonInfo } = state;
    if (rentRoomInfos.length && channelInfo.channelId) {
      const roomTypeIds: number[] = []; // 去重的房型列表
      const typeIdMap: { [key: number]: boolean } = {}; // 房型id map
      (rentRoomInfos || []).forEach((item) => {
        const { roomTypeId } = item;
        // 已有的房型id
        if (typeIdMap[roomTypeId]) return;
        roomTypeIds.push(roomTypeId);
        typeIdMap[roomTypeId] = true;
      });
      const { occType, businessDate, rentTime, expectedDepartureTime } = rentTimeInfo;
      const { channelId, strategyId } = channelInfo;
      const res = await BaseService.getRoomPrice({
        channelId,
        endDate: format2DateStr(expectedDepartureTime),
        occDuration: occType === OccType.DayUse ? undefined : rentTime, // 日租房不传
        occType,
        roomTypeIds,
        startDate: format2DateStr(businessDate), // 开始日期要传营业日 营业日小于等于当前日期
        strategyId,
        wyuid: commonInfo.ownerWYId,
        memberLevel: commonInfo.memberLevel,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取可选策略
const getStrategyList: (state: MainState) => Promise<StrategyItem[]> = async (state) => {
  let result: StrategyItem[] = [];
  try {
    const { commonInfo, channelInfo, rentTimeInfo } = state;
    if (channelInfo.channelId) {
      // 已经选择渠道了
      const res = await BaseService.getStrategyList({
        channelId: channelInfo.channelId,
        memberLevel: commonInfo.memberLevel,
        occDuration: rentTimeInfo.rentTime,
        occType: rentTimeInfo.occType,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

/**
 * 更新loading状态
 * 加个timer防止重复修改时闪烁
 */
let loadingTimer;
export const setLoadingStatus: (isLoading: boolean) => void = (isLoading) => {
  if (loadingTimer) {
    clearTimeout(loadingTimer);
  }
  return actionWrapper((dispatch, getState) => {
    // 和当前状态不一样
    loadingTimer = setTimeout(() => {
      loadingTimer = null;
      if (getState().isLoading !== isLoading) {
        dispatch({
          type: CheckInEditorAction.setLoadingStatus,
          payload: isLoading,
        });
      }
    });
  });
};

// 更新初始化状态
const setInitStatus = (isInit: boolean) => {
  return { type: CheckInEditorAction.setInitStatus, payload: isInit };
};

// 更新公共信息
const setCommonData: (data: Partial<CommonInfo>) => void = (data) => {
  return { type: CheckInEditorAction.setCommonData, payload: data };
};

// 更新入住时间信息
const updateRentTimeInfo = (rentTimeInfo: RentTimeInfo) => {
  return {
    type: CheckInEditorAction.setRentTimeData,
    payload: rentTimeInfo,
  };
};

// 更新客源渠道信息
const setChannelData = (data: Partial<SourceChannelInfo>) => {
  return {
    type: CheckInEditorAction.setChannelData,
    payload: data,
  };
};

// 更新策略列表
const setStrategyList = (data: StrategyItem[]) => {
  return {
    type: CheckInEditorAction.setStrategyList,
    payload: data,
  };
};

// 更新房价列表
const setPriceList = (list: ConfigRoomPrice[]) => {
  return {
    type: CheckInEditorAction.setRoomPriceList,
    payload: list,
  };
};

// 更新入住房间信息
const setRentList: (list: EditRentRoomInfo[]) => void = (list) => {
  return {
    type: CheckInEditorAction.setRentRoomList,
    payload: list,
  };
};

// 过滤入住日期 仅返回价格可修改的日期
const filterRentDateArr = (rentTime: RentTimeInfo, roomList: EditRentRoomInfo[]) => {
  const rentDateArr = getRentDayArr({ rentTimeInfo: rentTime, occType: rentTime.occType });
  const originDateArr = roomList && roomList.length ? roomList[0].roomPriceList || [] : [];
  const originDateMap = {}; // 保留的日期 这些日期的数据不可修改
  originDateArr.forEach((item) => {
    if (item.isBeforeAudit) {
      // 已过夜审
      originDateMap[item.rentDate] = true;
    }
  });
  return rentDateArr.filter((item) => !originDateMap[item.rentDate]);
};
/**
 * 更新房间信息
 * 1、更新房间入住时间信息
 * 2、已过夜审的不可删除、修改
 * 3、清空已选优惠券信息
 * 4、更新房间押金
 * 5、更新房间默认价格
 */
const updateRentRoomInfo = () => {
  return actionWrapper(async (dispatch, getState) => {
    const {
      rentRoomInfos,
      roomPriceList,
      rentTimeInfo,
      originRoomPriceMap,
      commonInfo,
    } = getState();
    const { cachePriceMap, cacheDepositMap } = flatPriceList(roomPriceList);
    const emptyCouponData = getEmptyCouponData();
    // 入住日期
    const rentDateArr = filterRentDateArr(rentTimeInfo, rentRoomInfos);
    const { occType } = rentTimeInfo;
    const { isDirectChannel } = commonInfo;

    // 更新房间信息
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const itemPriceList = roomItem.roomPriceList || [];
          const typePriceMap = cachePriceMap[roomItem.roomTypeId] || {};
          const originPriceMap = originRoomPriceMap[roomItem.roomId] || {};
          const updatePriceList: EditRoomPriceInfo[] = []; // 更新的房价列表信息
          itemPriceList.forEach((dayPrice) => {
            if (dayPrice.isBeforeAudit) {
              // 已过夜审 不可修改
              updatePriceList.push(dayPrice);
            }
          });
          rentDateArr.forEach((item) => {
            const { rentDate, rentDateId } = item;
            // 直连渠道取原价 策略价格兜底
            const inputPrice =
              (isDirectChannel ? originPriceMap[rentDate] : null) || typePriceMap[rentDate];
            updatePriceList.push({
              ...emptyCouponData,
              occType,
              isBeforeAudit: false,
              rentDate,
              rentDateId,
              inputPrice,
            });
          });
          return {
            ...roomItem,
            deposit: cacheDepositMap[roomItem.roomTypeId],
            roomPriceList: updatePriceList,
          };
        }),
      ),
    );
  });
};

// 清空房间优惠券
const clearCouponInfo = () => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    if (rentRoomInfos && rentRoomInfos.length) {
      const emptyCouponData = getEmptyCouponData();
      dispatch(
        setRentList(
          rentRoomInfos.map((item) => {
            const priceList = item.roomPriceList || [];
            return {
              ...item,
              roomPriceList: priceList.map((dayPrice) => {
                // 当前日期已过夜审 不可操作
                if (dayPrice.isBeforeAudit) return dayPrice;

                return {
                  ...dayPrice,
                  ...emptyCouponData,
                };
              }),
            };
          }),
        ),
      );
    }
  });
};

// 刷新房间定价
const refreshPriceList = (shouldUpdateRoom: boolean) => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { rentRoomInfos } = state;
    // 没有选中房间
    if (!rentRoomInfos.length) return;
    dispatch(setLoadingStatus(true));
    const priceList = await getRoomPriceList(state);
    dispatch(setPriceList(priceList));
    if (shouldUpdateRoom) {
      // 同步更新房间房价信息
      dispatch(updateRentRoomInfo());
    }
    dispatch(setLoadingStatus(false));
  });
};

/**
 * 是否过夜审
 * 钟点房 根据是否抛账来判断
 * 日租房 是否抛账 或者 营业日
 *  */
const isAfterAudit: (roomList: EditRentRoomInfo[]) => boolean = (roomList) => {
  let result = false;
  if (roomList && roomList[0] && roomList[0].roomPriceList) {
    const priceList = roomList[0].roomPriceList;
    // 房价列表中有过夜审（抛账）的
    result = priceList.some((item) => item.isBeforeAudit);
  }
  return result;
};

/**
 * 关联数据初始化
 * 1、渠道列表
 * 2、策略列表
 * 3、当前所在营业日
 * 4、预抵时间所在营业日
 * 5、校验入住日期是否已过夜审
 */
const initExtraData = () => {
  return actionWrapper(async (dispatch, getState) => {
    try {
      dispatch(setLoadingStatus(true));
      const state = getState();
      const { rentTimeInfo, channelInfo, rentRoomInfos, originBaseInfo } = state;
      const [
        allChannelList,
        channelList,
        strategyList,
        currentBusinessDate,
        ETABusinessDate,
      ] = await Promise.all([
        getAllChannelList(),
        getChannelList(),
        getStrategyList(state),
        getBusinessDate(DateJS()),
        getBusinessDate(rentTimeInfo.expectedArrivalTime),
      ]);

      // 原先选中的渠道信息
      const targetChannel = findItemByKey(allChannelList, 'id', channelInfo.channelId);
      // 是否为直连渠道
      const isDirectChannel = targetChannel ? targetChannel.directFlag : false;
      // 更新可选渠道列表
      dispatch({
        type: CheckInEditorAction.setChannelList,
        payload: isDirectChannel ? allChannelList : channelList, // 直连、非直连取不同的列表
      });

      const afterAudit = isAfterAudit(rentRoomInfos); // 是否过夜审
      let availableStrategyList = strategyList; //  可用策略列表
      if (afterAudit || isDirectChannel) {
        // 已过夜审 或 直连渠道 策略不可修改 策略列表取当前渠道下所有策略
        // 2025-04-27(v2.24计价策略修改迭代)-因后端无法修改targetChannel.priceStrategies的showName,所以前端 在策略list里面 取reservation/modify/detail里面的priceStrategyId进行匹配，将list的showName 强改成detail里面的priceStrategyName; （后端：吴玉林）
        const targetChannelPriceStrategies = (targetChannel.priceStrategies || []).map((item) => {
          if (originBaseInfo?.priceStrategyId === item?.id) {
            return { ...item, showName: originBaseInfo?.priceStrategyName };
          }
          return { ...item };
        });
        availableStrategyList = targetChannelPriceStrategies;
      }
      if (!afterAudit) {
        // 未过夜审
        const originStrategyId = channelInfo.strategyId; // 订单原策略id
        if (!availableStrategyList.some((item) => item.id === originStrategyId)) {
          // 原策略不存在 策略清空
          dispatch(setChannelData({ strategyId: null }));
        }
      }
      // 更新策略列表
      dispatch(setStrategyList(availableStrategyList));

      // 是否为日租房
      const isDayType = rentTimeInfo.occType === OccType.DayUse;
      let auditPassedCount = 0; // 已过夜审次数 不校验日租房
      if (isDayType && DateJS(currentBusinessDate).isAfter(ETABusinessDate, 'day')) {
        // 日租房 且 当前时间所在营业日 在 预抵时间所在营业日之后
        // 已过夜审次数 按照每天一次计算
        auditPassedCount = DateJS(currentBusinessDate).diff(ETABusinessDate, 'days');
      }
      // 更新上次夜审时间
      dispatch(
        setCommonData({
          isDirectChannel,
          auditPassedCount,
        }),
      );
      // 更新预抵时间所在营业日
      dispatch(updateRentTimeInfo({ ...rentTimeInfo, businessDate: ETABusinessDate }));
      dispatch(setInitStatus(true));
      dispatch(setLoadingStatus(false));
    } catch (e) {
      //
    }
  });
};

// 页面初始化
export const initPageState: (initFunc: () => Promise<MainState>) => void = (initFunc) => {
  return async (dispatch) => {
    try {
      // 未初始化状态
      dispatch({
        type: CheckInEditorAction.initDefaultState,
        payload: <ReducerState>{
          isInit: false,
          isLoading: true,
          originCouponList: [],
          roomPriceList: [],
          rentRoomInfos: [],
          commonInfo: {},
          rentTimeInfo: {},
          channelInfo: {},
          originRoomPriceMap: {},
        },
      });
      // 调用初始化方法
      const initState = await initFunc();
      // 设置初始化数据
      dispatch({ type: CheckInEditorAction.initDefaultState, payload: initState });
      dispatch(initExtraData());
      // 刷新价格
      dispatch(refreshPriceList(false));
    } catch (_) {
      dispatch(setInitStatus(false));
    }
  };
};

// 日租房时间改变
const handleDayTimeChange: (data: Partial<RentTimeInfo>) => void = (data) => {
  return actionWrapper(async (dispatch, getState) => {
    const { rentTimeInfo } = getState();
    const updateDate: RentTimeInfo = { ...rentTimeInfo, ...data };
    updateDate.expectedDepartureTime = getDayDepartureTime(
      updateDate,
      format2TimeStr(updateDate.expectedDepartureTime),
    ); // 计算离店时间
    dispatch(updateRentTimeInfo(updateDate)); // 更新时间信息
    dispatch(refreshPriceList(true));
  });
};

// 钟点房时间改变
const handleHourTimeChange: (data: Partial<RentTimeInfo>) => void = (data) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelInfo, rentTimeInfo } = getState();
    const updateDate = { ...rentTimeInfo, ...data };
    // 抵店时间和入住时长都有 更新离店时间
    if (updateDate.expectedArrivalTime && updateDate.rentTime) {
      updateDate.expectedDepartureTime = DateJS(updateDate.expectedArrivalTime).add(
        updateDate.rentTime,
        'hour',
      );
    }
    dispatch(updateRentTimeInfo(updateDate)); // 更新时间信息
    if (data.rentTime && channelInfo.channelId) {
      // 入住时长修改 且已选渠道 更新计价策略
      dispatch(setLoadingStatus(true));
      const strategyList = await getStrategyList(getState());
      dispatch(setStrategyList(strategyList));
      // 默认选中第一个策略
      const defaultStrategy = strategyList[0];
      dispatch(setChannelData({ strategyId: defaultStrategy?.id }));
      dispatch(setLoadingStatus(false));
      // 钟点房入住时长改变 刷新房价
      dispatch(refreshPriceList(true));
    }
  });
};

// 入住时间修改
export const handleRentTimeChange: (data: Partial<RentTimeInfo>) => void = (data) => {
  return actionWrapper(async (dispatch, getState) => {
    const { rentTimeInfo, commonInfo } = getState();
    // 直连渠道不可修改
    // V1.35 直连可修改
    // if (commonInfo.isDirectChannel) return;
    if (rentTimeInfo.occType === OccType.DayUse) {
      // 当前为日租房
      dispatch(handleDayTimeChange(data));
    }
    if (rentTimeInfo.occType === OccType.TimeRENT) {
      // 当前为钟点房
      dispatch(handleHourTimeChange(data));
    }
  });
};

// 根据策略配置更新日租房预离时间
const setDayETDByStrategy: (
  strategy: StrategyItem,
  callback?: (isChange: boolean) => void,
) => void = (strategy, callback) => {
  return actionWrapper((dispatch, getState) => {
    const { rentTimeInfo } = getState();
    let isETDChange = false;
    if (strategy?.departureDate) {
      // 根据策略的离店时间 计算日租房的离店时间
      const departureTime = getDayDepartureTime(rentTimeInfo, strategy.departureDate);
      isETDChange =
        format2TimeStamp(departureTime) !== format2TimeStamp(rentTimeInfo.expectedDepartureTime); // 离店时间改变
      if (isETDChange) {
        // 更新离店时间
        dispatch(updateRentTimeInfo({ ...rentTimeInfo, expectedDepartureTime: departureTime }));
      }
    }
    if (typeof callback === 'function') {
      callback(isETDChange);
    }
  });
};

// 渠道id改变
const handleChannelIdChange = (channelId: number) => {
  return actionWrapper(async (dispatch, getState) => {
    const { channelList, rentTimeInfo, originBaseInfo } = getState();
    const targetChannel = findItemByKey(channelList, 'id', channelId);
    if (targetChannel && targetChannel.type !== SourceChannelType.OTA) {
      // 非OTA渠道 清空渠道单号 预付款
      dispatch(
        setChannelData({
          channelId,
          strategyId: undefined,
          channelOrderNo: '', // =。=产品说这个字段保留原始值 20221116 又不保留了
        }),
      );
    } else {
      // 更新渠道 策略
      dispatch(setChannelData({ channelId, strategyId: undefined }));
    }
    dispatch(clearCouponInfo()); // 渠道修改(部分渠道不可用券) 清空已选优惠券
    // 更新计价策略
    dispatch(setLoadingStatus(true));
    const state = getState();
    const strategyList = await getStrategyList(state);
    dispatch(setStrategyList(strategyList));
    // 默认选中第一个策略
    const defaultStrategy = strategyList[0];
    dispatch(setChannelData({ channelId, strategyId: defaultStrategy?.id }));
    if (rentTimeInfo.occType === OccType.DayUse && !originBaseInfo.renewalId) {
      // 日租房 且不是修改的续住记录
      // 根据策略变更离店时间
      dispatch(setDayETDByStrategy(defaultStrategy));
    }
    dispatch(setLoadingStatus(false));
    // 更新房型价格
    dispatch(refreshPriceList(true));
  });
};

// 策略改变
const handleStrategyChange = (strategyId: number) => {
  return actionWrapper(async (dispatch, getState) => {
    const { rentTimeInfo, strategyList, originBaseInfo } = getState();
    dispatch(setChannelData({ strategyId }));
    const selectItem = findItemByKey(strategyList, 'id', strategyId);
    if (rentTimeInfo.occType === OccType.DayUse && !originBaseInfo.renewalId) {
      // 日租房 且不是修改的续住记录
      // 根据策略变更离店时间
      dispatch(setDayETDByStrategy(selectItem));
    }
    // 更新房型价格
    dispatch(refreshPriceList(true));
  });
};

// 客源渠道修改
export const handleChannelDataChange: (data: Partial<SourceChannelInfo>) => void = (data) => {
  return actionWrapper((dispatch, getState) => {
    const { commonInfo } = getState();
    // 直连渠道不可修改
    if (commonInfo.isDirectChannel) return;
    const { channelId, strategyId } = data;
    if (channelId != null) {
      dispatch(handleChannelIdChange(channelId));
    } else if (strategyId != null) {
      dispatch(handleStrategyChange(strategyId));
    } else {
      dispatch(setChannelData(data));
    }
  });
};

// 入住房间修改
export const handleRentItemChange: (
  indexId: string,
  updateValue: Partial<EditRentRoomInfo>,
) => void = (indexId, updateValue) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          if (item.indexId === indexId) {
            return {
              ...item,
              ...updateValue,
            };
          }
          return item;
        }),
      ),
    );
  });
};

// 更新备注信息
export const handleRemarkInfoChange: (data: Partial<RemarkInfo>) => void = (data) => {
  return actionWrapper((dispatch) => {
    dispatch({
      type: CheckInEditorAction.setRemarkInfo,
      payload: data,
    });
  });
};

// 批量修改房间券
export const batchUpdateRoomCoupon = (data: Record<string, Record<string, CouponValue>>) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    // 更新券
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const roomCouponMap = data[roomItem.indexId];
          if (!roomCouponMap) return roomItem;
          const itemPriceList = roomItem.roomPriceList || [];
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              if (dayItem.isBeforeAudit) return dayItem; // 已过夜审 不可修改
              // 当前间夜券有调整
              if (roomCouponMap[dayItem.rentDateId]) {
                return {
                  ...dayItem,
                  ...roomCouponMap[dayItem.rentDateId],
                };
              }
              return dayItem;
            }),
          };
        }),
      ),
    );
  });
};

// 获取全部状态
export const getMainState: (callback: (state: ReducerState) => void) => void = (callback) => {
  return actionWrapper((_, getState) => {
    try {
      callback(getState());
    } catch (e) {
      //
    }
  });
};
